import { auth } from '../services/firebaseService';

/**
 * Refresh Firebase token if it's close to expiring
 * Firebase tokens expire after 1 hour, so we refresh if less than 10 minutes remain
 * @returns {Promise<string|null>} - Refreshed token or null if refresh failed
 */
export const refreshFirebaseTokenIfNeeded = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    // Get current token to check expiration
    const currentToken = await user.getIdToken();
    
    // Decode token to check expiration (JWT format: header.payload.signature)
    try {
      const payload = JSON.parse(atob(currentToken.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;
      
      // Refresh if less than 10 minutes remaining (600000 ms)
      if (timeUntilExpiration < 600000) {
        console.log('Token expiring soon, refreshing...');
        const refreshedToken = await user.getIdToken(true); // Force refresh
        localStorage.setItem('firebaseToken', refreshedToken);
        localStorage.setItem('token', refreshedToken);
        return refreshedToken;
      }
      
      return currentToken;
    } catch (decodeError) {
      // If we can't decode, try to refresh anyway
      console.warn('Could not decode token, attempting refresh:', decodeError);
      const refreshedToken = await user.getIdToken(true);
      localStorage.setItem('firebaseToken', refreshedToken);
      localStorage.setItem('token', refreshedToken);
      return refreshedToken;
    }
  } catch (error) {
    console.error('Error refreshing Firebase token:', error);
    return null;
  }
};

/**
 * Force refresh Firebase token
 * @returns {Promise<string|null>} - Refreshed token or null if refresh failed
 */
export const forceRefreshFirebaseToken = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    const refreshedToken = await user.getIdToken(true); // Force refresh
    localStorage.setItem('firebaseToken', refreshedToken);
    localStorage.setItem('token', refreshedToken);
    return refreshedToken;
  } catch (error) {
    console.error('Error forcing Firebase token refresh:', error);
    return null;
  }
};

/**
 * Check if there's an active import job (stored in sessionStorage or state)
 * This helps determine if we should gracefully handle 401 errors
 */
export const hasActiveImport = () => {
  // Check sessionStorage for active import job ID
  const importJobId = sessionStorage.getItem('activeImportJobId');
  return !!importJobId;
};

/**
 * Set active import job ID
 */
export const setActiveImportJobId = (jobId) => {
  if (jobId) {
    sessionStorage.setItem('activeImportJobId', jobId);
  } else {
    sessionStorage.removeItem('activeImportJobId');
  }
};

